/**
 * Trade arithmetic for trade mode (TRADE-1 step 3b). Pure (no React), checked on its own by
 * tradeMath.check.mjs. Pedro's rules (OPEN_ITEMS TRADE-1):
 *   - the unit is Quantity, Value (Ref Ccy) or % of portfolio, and converts back and forth (TQ2);
 *   - every quantity is checked against the security's Min piece / Min increment and adjusted
 *     after input (TQ6): down to a multiple of the increment (1 unit when a quantity comes from
 *     a value or % and there is none); below the Min piece it becomes 0;
 *   - no short sales: a sale is capped at the quantity held; selling all of it is always allowed;
 *   - a value is the line's value per unit × quantity, accrued interest included (ACC-1: the
 *     View portfolio Value is the dirty value), in the currency shown.
 */

export type Unit = 'quantity' | 'value' | 'pct';
export type Side = 'buy' | 'sell';

export interface LotRules { minPiece: number | null; minIncrement: number | null }

/** The quantity an amount in `unit` stands for, before any lot adjustment. */
export function toQuantity(amount: number, unit: Unit, unitValue: number | null, gross: number | null): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (unit === 'quantity') return amount;
  if (!unitValue || unitValue <= 0) return null;
  if (unit === 'value') return amount / unitValue;
  if (!gross || gross <= 0) return null;
  return (amount / 100) * gross / unitValue;
}

/** A quantity expressed in `unit` (the inverse of toQuantity; TQ2 conversions). */
export function fromQuantity(q: number, unit: Unit, unitValue: number | null, gross: number | null): number | null {
  if (unit === 'quantity') return q;
  if (!unitValue) return null;
  if (unit === 'value') return q * unitValue;
  if (!gross) return null;
  return (q * unitValue / gross) * 100;
}

/** I6 Target % (Pedro 2026-10-23): the trade that takes a line from its value now to `wantPct`
 *  of the gross: buy or sell the difference (before the lot rules). Null when it cannot be
 *  worked out; side null when the line is already there. */
export function targetTrade(wantPct: number, held: number, unitValue: number | null, gross: number | null):
  { side: Side | null; quantity: number } | null {
  if (!Number.isFinite(wantPct) || wantPct < 0 || !unitValue || unitValue <= 0 || !gross || gross <= 0) return null;
  const delta = (wantPct / 100) * gross - held * unitValue;
  if (Math.abs(delta) <= 1e-9) return { side: null, quantity: 0 };
  return { side: delta > 0 ? 'buy' : 'sell', quantity: Math.abs(delta) / unitValue };
}

const EPS = 1e-9;

/** TP-LOT (Pedro 2026-10-24: a rebalance "left 100 and 1 unit lots … needs to be overridden if
 *  we are doing something that intends to bring the new total to 0"), narrowed by TP-LOT2 (Pedro
 *  2026-10-25: "only 0% sells everything"): a Target % of 0 sells the whole holding, so lot
 *  rounding never leaves a stub; a target above 0 is an explicit wish to keep some, so it never
 *  empties the line (see `adjust`, which also keeps at least the Min piece). */
export function targetSellsAll(wantPct: number): boolean {
  return !Number.isFinite(wantPct) || wantPct <= 0;
}

export interface Adjusted { quantity: number; note: string | null }

/** TQ6 + no short sales. `typedQuantity`: the user typed the quantity itself (unit Quantity),
 *  so without a Min increment it is kept as typed; a quantity worked out from a value or % is
 *  rounded down to whole units when the security has no increment. */
export function adjust(q: number, side: Side, held: number, lots: LotRules, typedQuantity: boolean): Adjusted {
  const notes: string[] = [];
  let x = q;
  if (side === 'sell' && x >= held - EPS) {
    // Selling everything held is always allowed (the server allows it too).
    return { quantity: held, note: x > held + EPS ? `capped at the ${fmtQ(held)} held (no short sales)` : null };
  }
  const inc = lots.minIncrement && lots.minIncrement > 0 ? lots.minIncrement : (typedQuantity ? null : 1);
  if (inc) {
    const down = Math.floor((x + EPS) / inc) * inc;
    if (Math.abs(down - x) > EPS) notes.push(`rounded down to a multiple of ${fmtQ(inc)}`);
    x = down;
  }
  if (lots.minPiece && q > 0 && x < lots.minPiece - EPS) {
    notes.push(`below the minimum lot of ${fmtQ(lots.minPiece)}`);
    x = 0;
  }
  // TP-LOT2 (Pedro 2026-10-25): a sale short of the whole holding must not leave less than the
  // Min piece behind (an untradeable remainder); it sells less, keeping one minimum lot.
  if (side === 'sell' && x > EPS && lots.minPiece && held - x > EPS && held - x < lots.minPiece - EPS) {
    let sale = held - lots.minPiece;
    if (inc) sale = Math.floor((sale + EPS) / inc) * inc;
    notes.push(`reduced to keep the minimum lot of ${fmtQ(lots.minPiece)}`);
    x = sale >= lots.minPiece - EPS ? sale : 0;   // the sale itself still meets the minimum lot
    if (x === 0) notes.push('nothing can be sold without selling the whole holding');
  }
  return { quantity: x, note: notes.length ? notes.join('; ') : null };
}

/** TP-XFER (Pedro 2026-10-24: "offer an efficient way to decide how much and from which
 *  account(s) cash will be transferred"): a suggested split covering each overdrawn account's
 *  shortfall from the accounts with cash to spare after the trades — an account in the same
 *  currency first, then the one with the most to spare; never more than an account can spare.
 *  Amounts in the currency shown. What cannot be covered stays uncovered (the user sees it). */
export interface CashNeed { code: string; ccy: string; need: number }
export interface CashSpare { code: string; ccy: string; spare: number }
export function suggestTransfers(needs: CashNeed[], spares: CashSpare[]): { from: string; to: string; amount: number }[] {
  const left = new Map(spares.map((s) => [s.code, Math.max(0, s.spare)]));
  const out: { from: string; to: string; amount: number }[] = [];
  for (const nd of needs) {
    let need = nd.need;
    const order = [...spares].filter((s) => s.code !== nd.code)
      .sort((a, b) => (Number(b.ccy === nd.ccy) - Number(a.ccy === nd.ccy)) || ((left.get(b.code) ?? 0) - (left.get(a.code) ?? 0)));
    for (const s of order) {
      if (need <= EPS) break;
      const can = left.get(s.code) ?? 0;
      if (can <= EPS) continue;
      const take = Math.min(can, need);
      out.push({ from: s.code, to: nd.code, amount: take });
      left.set(s.code, can - take);
      need -= take;
    }
  }
  return out;
}

/** M-REB (Pedro 2026-10-26: "set cash using transfers"): a model's cash per currency as targets.
 *  `accounts`: each cash account after the trades (the currency shown); `targets`: the amount the
 *  model wants in each currency (the currency shown). Per currency, the shortfall goes to its
 *  first account (a need); a surplus is offered from its accounts in order, never more than an
 *  account holds (spares); a currency with no target counts as 0 (all of it may move). A target
 *  currency with no cash account cannot be reached and is named in `missing` — never skipped
 *  silently (Pedro: "never silently skip anything"). Feed the result to `suggestTransfers`. */
export interface CashAccount { code: string; ccy: string; balance: number }
export function planCashTargets(accounts: CashAccount[], targets: Record<string, number>):
  { needs: CashNeed[]; spares: CashSpare[]; missing: string[] } {
  const ccys = [...new Set(accounts.map((a) => a.ccy))];
  const missing = Object.keys(targets).filter((c) => !ccys.includes(c) && targets[c] > 0.005);
  const needs: CashNeed[] = [];
  const spares: CashSpare[] = [];
  for (const c of ccys) {
    const accs = accounts.filter((a) => a.ccy === c);
    const cur = accs.reduce((s, a) => s + a.balance, 0);
    const diff = (targets[c] ?? 0) - cur;
    if (diff > 0.005) needs.push({ code: accs[0].code, ccy: c, need: diff });
    else if (diff < -0.005) {
      let surplus = -diff;
      for (const a of accs) {
        if (surplus <= 0.005) break;
        const give = Math.min(Math.max(a.balance, 0), surplus);
        if (give > 0.005) { spares.push({ code: a.code, ccy: c, spare: give }); surplus -= give; }
      }
    }
  }
  return { needs, spares, missing };
}

/** MR-RES (Pedro 2026-10-26: "Yes, empty currencies the model doesn't hold"): the lot rules leave
 *  a little more (or less) cash than the model holds. Any EXCESS — the cash after the trades above
 *  the model's targets in the currencies this portfolio has an account in — is added to the
 *  target of the model's main cash currency (its largest target with an account), so currencies
 *  the model does not hold are emptied in full and the excess is in one known place. Returns the
 *  new targets, the excess (the currency shown) and where it goes (null: no model cash currency
 *  has an account here, so nothing is swept — the caller names it). */
export function sweepExcess(accounts: CashAccount[], targets: Record<string, number>):
  { targets: Record<string, number>; excess: number; into: string | null } {
  const have = new Set(accounts.map((a) => a.ccy));
  const total = accounts.reduce((s, a) => s + a.balance, 0);
  const reachable = Object.entries(targets).filter(([c]) => have.has(c));
  const excess = total - reachable.reduce((s, [, t]) => s + t, 0);
  const into = reachable.sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
  if (excess <= 0.005 || into == null) return { targets: { ...targets }, excess, into: excess > 0.005 ? null : into };
  return { targets: { ...targets, [into]: targets[into] + excess }, excess, into };
}

/** TC-S5-CASHPLAN (Pedro 2026-11-02, "yes"; on "We could keep them within the same bank if we covered
 *  30-950001-11 with 30-950001-12 and 30-940003-11 with 30-940003-14"): a consolidation's cash, portfolio
 *  by portfolio. Within each portfolio (no cash leaves it, so its minimum cash is untouched): every
 *  account below zero is brought to zero, and — with a model (`pct`, % per currency) — each currency is
 *  brought to the model's % of THAT portfolio's value (its excess swept into the model's main currency,
 *  as `sweepExcess`). Only an overdraft the portfolio cannot cover itself is then covered from the
 *  consolidation's other portfolios: never across wrappers (null and a label differ), the same bank
 *  first, never taking a portfolio below its minimum cash; each such transfer is `cross`. A portfolio below
 *  its own minimum cash is topped up the same way (TC-S5-ONESTEP). What is left
 *  uncovered is returned in `short` (never skipped silently). All amounts in the currency shown. */
export interface PlanPortfolio { p: string; bank: string; wrapper: string | null; value: number; minCash: number }
export interface PlanAccount { code: string; p: string; ccy: string; balance: number }
export interface PlannedTransfer { from: string; to: string; amount: number; cross: 'bank' | 'portfolio' | null }
export function planConsolidationCash(accounts: PlanAccount[], pfs: PlanPortfolio[], pct: Record<string, number> | null):
  { transfers: PlannedTransfer[]; short: { code: string; amount: number }[]; missing: string[]; unswept: string[] } {
  const bal = new Map(accounts.map((a) => [a.code, a.balance]));
  const out: PlannedTransfer[] = [];
  const move = (from: string, to: string, amount: number, cross: PlannedTransfer['cross']) => {
    out.push({ from, to, amount, cross });
    bal.set(from, (bal.get(from) ?? 0) - amount); bal.set(to, (bal.get(to) ?? 0) + amount);
  };
  const allCcys = new Set(accounts.map((a) => a.ccy));
  const missing = pct ? Object.keys(pct).filter((c) => !allCcys.has(c) && pct[c] > 0) : [];
  const unswept: string[] = [];
  const odLeft: CashNeed[] = [];
  const minLeft: CashNeed[] = [];
  for (const pf of pfs) {
    const accs = accounts.filter((a) => a.p === pf.p);
    if (!accs.length) continue;
    let targets: Record<string, number> | null = null;
    if (pct) {
      const sw = sweepExcess(accs, Object.fromEntries(Object.entries(pct).map(([c, x]) => [c, (x / 100) * pf.value])));
      targets = sw.targets;
      if (sw.excess > 0.005 && sw.into == null) unswept.push(pf.p);
    }
    const needs: (CashNeed & { od: boolean })[] = [];
    const spares: CashSpare[] = [];
    for (const c of [...new Set(accs.map((a) => a.ccy))]) {
      const ca = accs.filter((a) => a.ccy === c);
      const cur = ca.reduce((s, a) => s + a.balance, 0);
      const ods = ca.filter((a) => a.balance < -0.005);
      for (const a of ods) needs.push({ code: a.code, ccy: c, need: -a.balance, od: true });
      const odSum = ods.reduce((s, a) => s - a.balance, 0);
      // Without a model, every positive balance may cover an overdraft; with one, only the surplus over the target.
      let rem = targets ? (targets[c] ?? 0) - cur - odSum : -Infinity;
      if (rem > 0.005) needs.push({ code: (ca.find((a) => a.balance >= 0) ?? ca[0]).code, ccy: c, need: rem, od: false });
      for (const a of ca) {
        if (rem >= -0.005) break;
        const give = Math.min(Math.max(a.balance, 0), -rem);
        if (give > 0.005) { spares.push({ code: a.code, ccy: c, spare: give }); rem += give; }
      }
    }
    // Overdrafts are served first, then the model's targets.
    const ordered = [...needs.filter((n) => n.od), ...needs.filter((n) => !n.od)];
    for (const g of suggestTransfers(ordered, spares)) move(g.from, g.to, g.amount, null);
    for (const n of needs.filter((x) => x.od)) {
      const left = -(bal.get(n.code) ?? 0);
      if (left > 0.005) odLeft.push({ code: n.code, ccy: n.ccy, need: left });
    }
    // TC-S5-ONESTEP: a portfolio whose cash, once its overdrafts are counted, is below its minimum cash
    // gets the rest from another portfolio too — into its largest account.
    const total = accs.reduce((s, a) => s + (bal.get(a.code) ?? 0), 0);
    const odHere = odLeft.filter((n) => accs.some((a) => a.code === n.code)).reduce((s, n) => s + n.need, 0);
    const lack = pf.minCash - (total + odHere);
    if (lack > 0.005) {
      const into = [...accs].sort((a, b) => (bal.get(b.code) ?? 0) - (bal.get(a.code) ?? 0))[0];
      minLeft.push({ code: into.code, ccy: into.ccy, need: lack });
    }
  }
  odLeft.push(...minLeft);   // every overdraft is served before any minimum cash
  // What a portfolio cannot cover itself: from the others — same wrapper only, same bank first.
  const pfOf = (code: string) => pfs.find((x) => x.p === accounts.find((a) => a.code === code)?.p);
  const short: { code: string; amount: number }[] = [];
  for (const nd of odLeft) {
    const me = pfOf(nd.code)!;
    let need = nd.need;
    const donors = pfs.filter((d) => d.p !== me.p && d.wrapper === me.wrapper)
      .sort((a, b) => Number(b.bank === me.bank) - Number(a.bank === me.bank));
    for (const d of donors) {
      if (need <= 0.005) break;
      const daccs = accounts.filter((a) => a.p === d.p)
        .sort((a, b) => (Number(b.ccy === nd.ccy) - Number(a.ccy === nd.ccy)) || ((bal.get(b.code) ?? 0) - (bal.get(a.code) ?? 0)));
      let room = daccs.reduce((s, a) => s + (bal.get(a.code) ?? 0), 0) - d.minCash;
      for (const a of daccs) {
        if (need <= 0.005 || room <= 0.005) break;
        const take = Math.min(Math.max(bal.get(a.code) ?? 0, 0), need, room);
        if (take > 0.005) { move(a.code, nd.code, take, d.bank === me.bank ? 'portfolio' : 'bank'); need -= take; room -= take; }
      }
    }
    if (need > 0.005) short.push({ code: nd.code, amount: need });
  }
  // One transfer per pair of accounts (an overdraft and a model target served from the same account).
  const merged = new Map<string, PlannedTransfer>();
  for (const t of out) {
    const k = `${t.from}>${t.to}`;
    const m = merged.get(k);
    merged.set(k, m ? { ...m, amount: m.amount + t.amount } : { ...t });
  }
  return { transfers: [...merged.values()], short, missing, unswept };
}

const fmtQ = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 6 });
